// Copyright 2025, University of Maryland, All Rights Reserved
import type { UILayout as WorkspaceLayout } from '../types'
import type { BuilderLayout, BuilderWidget } from '../types'

/**
 * Default permissions assigned to new tabs.
 */
export const DEFAULT_TAB_PERMISSIONS = ['workspace_admin', 'workspace_user'] as const

/**
 * Create a unique widget ID for React keys and drag-drop tracking.
 * These IDs are ephemeral - regenerated on each load, not persisted to API.
 */
export const createWidgetId = (tabId: string, nodeId: string): string => {
  const random = typeof globalThis.crypto !== 'undefined' && 'randomUUID' in globalThis.crypto
    ? globalThis.crypto.randomUUID()
    : Math.random().toString(36).slice(2)
  return `${tabId}-${nodeId}-${random}`
}

/**
 * Convert a string to a URL-safe slug.
 */
export const slugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'tab'

/**
 * Ensure a tab ID is unique among existing IDs.
 */
export const ensureUniqueTabId = (existing: Set<string>, base: string): string => {
  let candidate = base
  let suffix = 2
  while (existing.has(candidate)) {
    candidate = `${base}-${suffix}`
    suffix += 1
  }
  return candidate
}

/**
 * Convert API layout to internal builder layout.
 * Adds ephemeral id/order fields to widgets for React keys and drag-drop.
 */
export const toBuilderLayout = (layout?: WorkspaceLayout | null): BuilderLayout => {
  const tabs = (layout?.tabs ?? []).map((tab, tabIndex) => ({
    ...tab,
    order: tab.order ?? tabIndex,
    permissions: tab.permissions?.length ? tab.permissions : [...DEFAULT_TAB_PERMISSIONS],
    widgets: (tab.widgets ?? []).map((widget, widgetIndex) => ({
      ...widget,
      id: createWidgetId(tab.id, widget.component),
      order: widgetIndex,
    })) as BuilderWidget[],
  }))

  return {
    tabs,
    defaultTab: layout?.defaultTab ?? tabs[0]?.id,
  }
}

/**
 * Convert internal builder layout to API layout.
 * Strips ephemeral id/order fields - API stores widgets by position in array.
 */
export const toApiLayout = (layout: BuilderLayout): WorkspaceLayout => ({
  tabs: layout.tabs.map((tab, tabIndex) => ({
    id: tab.id,
    label: tab.label,
    order: tab.order ?? tabIndex,
    permissions: tab.permissions?.length ? tab.permissions : [...DEFAULT_TAB_PERMISSIONS],
    widgets: [...tab.widgets]
      .sort((a, b) => a.order - b.order)
      .map(({ id: _id, order: _order, ...rest }) => rest),
  })),
  defaultTab: layout.defaultTab,
})
